import moment from 'moment-timezone';
import React, { useEffect, KeyboardEvent } from 'react';
import DOMPurify from 'dompurify';
import { regexForZipcode } from './yupvalidations';
import {
  ORDER_PLACED_STATUS,
  SHIFT_COVERAGE,
  SHOPPING_MODE,
} from '../helpers/contants';
import dayjs, { Dayjs } from 'dayjs';
import {
  BranchInfo,
  CartProduct,
  FilterParams,
  SegregatedCart,
} from '../types';

/**
 * Sanitizes API-sourced HTML before it's passed to dangerouslySetInnerHTML.
 * Use for any description/content field that isn't already trusted, static markup.
 */
export function sanitizeHtml(html?: string | null): string {
  return DOMPurify.sanitize(html ?? '');
}

export function toTitleCase(input: string): string {
  return input?.replace(/\w\S*/g, word => {
    return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
  });
}
export function generateRandomString(length: number): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const timestamp = new Date().getTime().toString(); // Get the current timestamp
  const randomCharacters = [];

  // Generate random characters
  while (randomCharacters.length < length - timestamp.length) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomCharacters.push(characters.charAt(randomIndex));
  }

  // Combine random characters with the timestamp
  const randomString = randomCharacters.join('') + timestamp;

  return randomString;
}
export const preventSubmitOnEnter = (e: KeyboardEvent<HTMLFormElement>) => {
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

// The block/unblock socket events only accept blockedUserType values of
// 'vendor' | 'user' | 'admin' | 'driver' - chat.businessChatType (e.g.
// 'user_user_chat', 'analyst_reporter_chat') must be mapped to that enum.
export const getBlockedUserType = (
  businessChatType?: string,
): 'vendor' | 'admin' | 'driver' | 'user' => {
  if (businessChatType?.includes('vendor')) return 'vendor';
  if (businessChatType?.includes('admin')) return 'admin';
  if (businessChatType?.includes('driver')) return 'driver';
  return 'user';
};
export const formatPrice = (price: string): string => {
  const parsed = parseFloat(price);
  if (Number.isNaN(parsed)) return '0';

  // Format with commas and two decimal places
  return parsed.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Parses API spending values (numbers or comma-formatted strings like "81,157,257.09"). */
export const parseSpendingAmount = (value: unknown): number => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const n = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Label for disabled filter inputs (string or { name } selection objects). */
export const getFilterSelectionDisplayValue = (
  value?: string | { name?: string } | null,
): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return value.name ?? '';
  return String(value);
};

export const validateZipCode = (code: string): boolean =>
  regexForZipcode.test(code);
export const getTimeWithTimeZoneAbbreviation = (dateString: any) => {
  const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = moment
    .utc(dateString)
    .tz(deviceTimeZone)
    .format('MMM D, YYYY  hh:mm A (z)');
  return formattedDate;
};
export const getDateZone = (dateString: any) => {
  const formattedDate = moment.utc(dateString).format('MMM D, YYYY');
  return formattedDate;
};
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
) {
  let timeoutId: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };
}
export const useDebounce = (value: any, delay: any) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 *
 * @param formRef
 * @param errors
 */
export function scrollErorrElementInView(formRef: any, errors: any) {
  if (Object.keys(errors).length > 0) {
    const errorField = formRef.current?.querySelector(`.Mui-error`);
    if (errorField) {
      errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

export function stableSort<T>(
  array: T[],
  comparator: (a: T, b: T) => number,
): T[] {
  const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

export function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  sortBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, sortBy)
    : (a, b) => -descendingComparator(a, b, sortBy);
}

export function descendingComparator<Key extends keyof any>(
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
  sortBy: Key,
): number {
  if (b[sortBy] < a[sortBy]) {
    return -1;
  }
  if (b[sortBy] > a[sortBy]) {
    return 1;
  }
  return 0;
}

export function isBase64URL(str: any) {
  // Regular expression to match the Base64 URL pattern
  const base64Regex = /^(data:image\/[a-zA-Z]*;base64,)[^\s]+$/;

  return base64Regex.test(str);
}

export const capitalizeFirstLetter = (str: any) => {
  if (!str) {
    return '';
  }
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
};

export async function fetchLocation(code: string) {
  const apiKey = process.env.GOOGLE_API_KEY; // Replace with your actual API key

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:${code}&key=${apiKey}`,
  );
  return response.json();
}

export function extractNumbersAndSpecialChars(inputString: any) {
  // Use a regular expression to match numbers and special characters
  const regex = /[0-9!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/g;
  const resultArray = inputString?.match(regex);

  // Convert the matched characters into a string
  const resultString = resultArray ? resultArray.join('') : '';

  return resultString;
}

export const removeAlphabets = (input: string): string => {
  return input.replace(/[^0-9+]/g, '');
};

export function isUnixTimestamp(value: any) {
  // Check if the value is a number
  if (typeof value === 'number' && !isNaN(value)) {
    // Check if the value is a valid Unix timestamp
    return moment(value, 'x', true).isValid();
  }
  return false;
}

export function truncateWithEllipsis(text: string, maxLength = 40): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}
export function TabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const formatDateWithTime = (isoDate: string): string => {
  const date = new Date(isoDate);

  // Array for month names
  const months: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const day: number = date.getDate(); // Day of the month
  const month: string = months[date.getMonth()]; // Month in short form
  let hours: number = date.getHours(); // Hours in 24-hour format
  let minutes: number | string = date.getMinutes(); // Minutes

  // Determine AM or PM
  const period: string = hours >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  hours = hours % 12;
  if (hours === 0) hours = 12; // If hour is 0, convert it to 12

  // Format minutes
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  // Return formatted date string
  return `${month} ${day}, ${hours}:${minutes} ${period}`;
};

export const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
};

export const formatDateUTC = (value: number | string): string => {
  const date =
    typeof value === 'number'
      ? new Date(value)
      : new Date(`${value}T00:00:00Z`);

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const formatShortDate = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export const checkGeolocationPermission = async () => {
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state; // Returns "granted", "denied", or "prompt"
  } catch (error) {
    console.error('Permission API not supported', error);
    return null;
  }
};

export const getCurrentLocation = (
  errCb?: () => void,
): Promise<{ latitude: number; longitude: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const error = {
        type: 'UNSUPPORTED',
        message: 'Geolocation is not supported by this browser.',
      };
      errCb?.();
      return reject(error);
    }

    const cached = localStorage.getItem('userLocation');
    const ts = localStorage.getItem('userLocationTimestamp');

    if (cached && ts && Date.now() - Number(ts) < 3600000) {
      return resolve(JSON.parse(cached));
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        const location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };

        localStorage.setItem('userLocation', JSON.stringify(location));
        localStorage.setItem('userLocationTimestamp', Date.now().toString());

        resolve(location);
      },
      err => {
        errCb?.();
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 3600000,
      },
    );
  });
};

export const fetchLocationIfPermitted = async () => {
  const permissionState = await checkGeolocationPermission();

  if (permissionState === 'granted') {
    return getCurrentLocation();
  } else if (permissionState === 'prompt') {
    // Only request location if we don't have a recent cached location
    const cachedLocation = localStorage.getItem('userLocation');
    const cachedTimestamp = localStorage.getItem('userLocationTimestamp');

    if (cachedLocation && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp);
      const now = Date.now();
      if (now - timestamp < 3600000) {
        return JSON.parse(cachedLocation);
      }
    }

    return getCurrentLocation();
  }
  console.warn('Location access denied by the user.');
  return null;
};

export const fetchAndCacheLocation = async () => {
  const cachedLocation = localStorage.getItem('userLocation');
  if (cachedLocation) {
    return JSON.parse(cachedLocation);
  }

  try {
    const location = await getCurrentLocation();
    localStorage.setItem('userLocation', JSON.stringify(location));
    return location;
  } catch (error) {
    console.error('Error fetching location', error);
  }
};

export const openGoogleMap = (
  sourceLocation: string,
  destinationLocation: string,
) => {
  const baseUrl = 'https://www.google.com/maps/dir/';
  const fullUrl = `${baseUrl}${sourceLocation}/${destinationLocation}`;
  window.open(fullUrl, '_blank');
};

const shiftCoverageList = Object.keys(SHIFT_COVERAGE);
/**
 *
 * @param startTime
 * @param endTime
 * @returns string
 */
export function isDayOrNight(startTime: number, endTime: number): string {
  console.log('=>>>>', startTime, endTime);
  // Convert times to 24-hour format (if needed)
  const startHour = startTime % 24;
  const endHour = endTime % 24;

  // Define day and night hours (adjust based on your location and season)
  const dayStartHour = 7; // 7:00 AM
  const nightStartHour = 19; // 7:00 PM

  // Check if the start time falls within day or night hours
  if (startHour >= dayStartHour && startHour < nightStartHour) {
    return shiftCoverageList[0];
  } else if (endHour >= dayStartHour && endHour < nightStartHour) {
    return shiftCoverageList[0];
  }
  return shiftCoverageList[1];
}

export const getAddress = async (lat: any, lng: any) => {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCu3pQ2rxalRAWIONNcxoYST8Cy7NJrveA`,
    );
    const data = await response.json();
    if (data.results && data.results.length > 0) {
      // setLocation((prev) => ({
      //   ...prev,
      //   address: data.results[0].formatted_address,
      // }));
      const locationName = data.results[0].formatted_address;
      // const locationName = parts[1] // Extract 'Khariwara'
      const zipcodeMatch = data.results[0].formatted_address.match(/\b\d{6}\b/); // Match 6-digit zipcode
      const zipcode = zipcodeMatch ? zipcodeMatch[0] : 'N/A';
      const coordinates = [lat, lng];
      return { locationName, zipcode, coordinates };
      // return data.results[0].formatted_address
    }
  } catch {
    // setLocation((prev) => ({
    //   ...prev,
    //   error: "Failed to fetch address",
    // }));

    return 'Failed to fetch address';
  }
};

export const formatToTwoDecimals = (number: any) => {
  if (isNaN(number)) return '';
  return parseFloat(number).toFixed(2);
};

export const generateTimeStamp = (startDate: any, startTime: any) => {
  if (!startDate) {
    return null; // or throw new Error("startDate is required");
  }

  const dateObj = new Date(startDate);
  if (isNaN(dateObj.getTime())) {
    return null; // Invalid date
  }

  const dateStr = `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${dateObj.getDate().toString().padStart(2, '0')}`;

  let timeStr = '00:00'; // default time if startTime is empty
  if (startTime) {
    const timeObj = new Date(startTime);
    if (!isNaN(timeObj.getTime())) {
      timeStr = `${timeObj.getHours().toString().padStart(2, '0')}:${timeObj
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }
  }

  const fullDateTime = `${dateStr} ${timeStr}`;
  const timestamp = dayjs(fullDateTime).toDate().getTime();

  return timestamp;
};

export const getStartAndEndDatesInISO = (startDate: any, endDate: any) => {
  if (!startDate || !endDate) {
    throw new Error('Both startDate and endDate are required.');
  }

  const startDateTime = dayjs(startDate).startOf('day'); // Set to 12:00 AM
  const endDateTime = dayjs(endDate).endOf('day'); // Set to 11:59 PM

  return {
    startDateISO: startDateTime.toISOString(),
    endDateISO: endDateTime.toISOString(),
  };
};

export const convertSecondsToTime = (totalSeconds: number, _item?: any) => {
  const hours = Math.floor(totalSeconds / 3600); // Get total hours
  const minutes = Math.floor((totalSeconds % 3600) / 60); // Get remaining minutes
  const seconds = totalSeconds % 60; // Get remaining seconds

  // If total seconds is less than or equal to 60, show only seconds
  if (totalSeconds <= 60) {
    const secondLabel = seconds === 1 ? 'sec' : 'secs';
    return `${seconds} ${secondLabel}`;
  }

  // Otherwise, show hours and minutes
  const hourLabel = hours === 1 ? 'hr' : 'hrs';
  const minuteLabel = minutes === 1 ? 'min' : 'mins';

  const timeParts = [];
  if (hours) timeParts.push(`${hours} ${hourLabel}`);
  if (minutes) timeParts.push(`${minutes} ${minuteLabel}`);

  return timeParts.join(' ');
};

export const dataSetter = (key: string, value: any) => {
  localStorage.setItem(key, value);
};

export const dataGetter = (key: any) => {
  const data: any = localStorage.getItem(key);
  return data;
};

//  Dynamic Conversion of Any Date to 00:00:00.000Z Without Timezone Shift
// If you want the date to always convert dynamically to midnight (00:00:00.000Z) in the user's local timezone, use this approach:
// import dayjs from "dayjs";
// import utc from "dayjs/plugin/utc";
// import timezone from "dayjs/plugin/timezone";

// dayjs.extend(utc);
// dayjs.extend(timezone);

// const convertToMidnightUTC = (dateString) => {
//   return dayjs(dateString).tz(dayjs.tz.guess()).startOf("day").utc().toISOString();
// };

// // Example Usage
// const startTimeISO = "2025-02-11T18:30:00.000Z"; // Input date (can be any valid ISO date)
// const formattedDate = convertToMidnightUTC(startTimeISO);

// console.log(formattedDate);

export function isValidFileUrl(
  url: any,
  allowedExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'pdf',
    'mp4',
    'mp3',
    'txt',
    'docx',
  ],
) {
  try {
    // ✅ Convert to URL object to check validity
    const parsedUrl: any = new URL(url);

    // ✅ Check if URL has a valid protocol (http, https, file)
    if (!['http:', 'https:', 'file:'].includes(parsedUrl.protocol)) {
      return false;
    }

    // ✅ Extract file extension
    const fileExtension: any = parsedUrl.pathname
      .split('.')
      .pop()
      .toLowerCase();

    // ✅ Check if the extension is in the allowed list
    return allowedExtensions.includes(fileExtension);
  } catch {
    // ❌ If URL parsing fails, it's not a valid URL
    return false;
  }
}

export const formattedDate = (schedule: any[]) => {
  // Find min start date and max end date
  const minStartDate = new Date(
    Math.min(...schedule?.map(item => item.startDate)),
  );
  const maxEndDate = new Date(Math.max(...schedule?.map(item => item.endDate)));

  // Format the dates
  return `${minStartDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} - ${maxEndDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

export const formatdDate = (minStartDate: number, maxEndDate: number) => {
  const start = new Date(minStartDate);
  const end = new Date(maxEndDate);
  return `${start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} - ${end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

export const getDiscountOff = (amount: any, nextLine?: boolean) => {
  return nextLine ? `${amount}% \nOFF` : `${amount}% OFF`;
};

export const getPriceWithCurrency = (amount: any, _withoutAbbri?: boolean) => {
  // const numericAmount = Math.abs(Number(amount)); // ensures value is positive

  // if (isNaN(numericAmount) || numericAmount === 0) return '0';

  // if (withoutAbbri) {
  //   return `$${formatNumberWithValue(numericAmount)}`;
  // }

  // return `$${abbreviatePrice(numericAmount)}`;

  return (
    '$' +
    Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })
  );
};

export const formatPriceInK = (value: number): string => {
  // if (value >= 1_000_000) {
  //   const mValue = Math.floor(value / 100_000) / 10; // Truncate to 1 decimal
  //   return `${mValue % 1 === 0 ? mValue.toFixed(0) : mValue}M`;
  // } else if (value >= 1_0000) {
  //   const kValue = Math.floor(value / 100) / 10; // Truncate to 1 decimal
  //   return `${kValue % 1 === 0 ? kValue.toFixed(0) : kValue}k`;
  // }
  // return Number(value).toLocaleString('en-US', {
  //   minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  //   maximumFractionDigits: 2,
  // });
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export function convertToQueryParams(filter: FilterParams): string {
  const params = new URLSearchParams();

  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });

  return params.toString(); // e.g. searchKey=abc&episodeIds=123
}

export const isSafari = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
);
//Set Coverage
export const coverageDetails = (val: any, shift: any) => {
  if (!val || !shift) {
    return `Day - ${shift ?? '12'} hrs Shift`;
  }
  let value;
  if (dayjs.isDayjs(val)) {
    value = val.format('HH:mm');
  } else {
    value = val;
  }

  if (typeof value !== 'string' || !value.includes(':')) {
    // Return default or throw error or handle invalid input gracefully
    return undefined; // or throw new Error('Invalid time format');
  }

  const [hourStr, minuteStr] = value.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return undefined; // or handle invalid time
  }

  const minutesSinceMidnight = hour * 60 + minute;

  if (minutesSinceMidnight >= 180 && minutesSinceMidnight < 900) {
    return `Day - ${shift} hrs Shift`;
  }

  // Night shift case: time < 3:00 or >= 15:00
  return `Night - ${shift} hrs Shift`;
};

export const cartShiftCoverageDetails = (val: any, shift: any) => {
  console.log('val', val);
  console.log('shift', shift);
  if (!val) {
    return `Day - ${shift} hrs Shift`;
  }

  let hour: number, minute: number;

  // Handle Date object
  if (val instanceof Date) {
    hour = val.getHours();
    minute = val.getMinutes();
  }
  // Handle dayjs object
  else if (dayjs.isDayjs(val)) {
    hour = val.hour();
    minute = val.minute();
  }
  // Handle string in HH:mm format
  else if (typeof val === 'string' && val.includes(':')) {
    const [hourStr, minuteStr] = val.split(':');
    hour = Number(hourStr);
    minute = Number(minuteStr);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return `Day - ${shift} hrs Shift`; // Default to day shift if parsing fails
    }
  } else {
    return `Day - ${shift} hrs Shift`; // Default to day shift for any other case
  }

  const minutesSinceMidnight = hour * 60 + minute;

  if (minutesSinceMidnight >= 180 && minutesSinceMidnight < 900) {
    return `Day - ${shift} hrs Shift`;
  }

  // Night shift case: time < 3:00 or >= 15:00
  return `Night - ${shift} hrs Shift`;
};

// pre populate End time

export function autoEndTime(
  startTime: Dayjs | string | undefined,
  shift: string,
  shiftLength: number,
): Dayjs | null {
  console.log('shiftLength', shiftLength);
  let parsedStart: Dayjs | null = null;
  if (!startTime) return null;
  if (dayjs.isDayjs(startTime)) parsedStart = startTime;
  else if (typeof startTime === 'string')
    parsedStart = dayjs(startTime, 'HH:mm');
  else return null;

  if (!parsedStart.isValid()) return null;

  return parsedStart.add(shiftLength, 'hour');
}

/**
 * Puts shift end on the correct calendar day relative to start.
 * End after start on the clock → same date as start (e.g. 10 PM → 11:30 PM).
 * End on/before start on the clock → next day (overnight, e.g. 10 PM → 8:30 AM).
 */
export function alignShiftEndToStart(
  start: Dayjs | Date | string,
  end: Dayjs | Date | string,
): Dayjs {
  const startD = dayjs(start);
  const endD = dayjs(end);

  // Built via the native Date constructor (not dayjs's chained .hour()/.add()
  // setters) so the JS engine resolves the correct UTC offset for the target
  // calendar day itself — chaining through a DST boundary can otherwise leave
  // a stale offset on the resulting dayjs instance.
  const sameDay = new Date(
    startD.year(),
    startD.month(),
    startD.date(),
    endD.hour(),
    endD.minute(),
    endD.second(),
    endD.millisecond(),
  );

  const aligned =
    sameDay.getTime() <= startD.valueOf()
      ? new Date(
          startD.year(),
          startD.month(),
          startD.date() + 1,
          endD.hour(),
          endD.minute(),
          endD.second(),
          endD.millisecond(),
        )
      : sameDay;

  return dayjs(aligned);
}

export interface ServiceDay {
  dayjs: Dayjs;
  date: string;
  name: string;
  isDisabled: boolean;
}

/**
 * Builds one calendar-day card per service/shift date between startTime and endTime.
 * For overnight shifts (end clock-time earlier than start clock-time, e.g. 8 PM -> 5 AM),
 * the raw end timestamp spills into the calendar day after the last shift actually
 * starts — that trailing day has no shift starting on it, so it's excluded here.
 */
export function getServiceDays(
  startTime?: string,
  endTime?: string,
): ServiceDay[] {
  if (!startTime || !endTime) return [];

  const start = dayjs(startTime);
  const end = dayjs(endTime);

  let totalDays = end.startOf('day').diff(start.startOf('day'), 'day') + 1;

  if (totalDays > 1 && end.format('HH:mm:ss') < start.format('HH:mm:ss')) {
    totalDays -= 1;
  }

  const allDays: ServiceDay[] = [];

  for (let d = 0; d < totalDays; d++) {
    const currentDay = start.add(d, 'day');

    allDays.push({
      dayjs: currentDay,
      date: currentDay.format('YYYY-MM-DD'),
      name: currentDay.format('ddd'),
      isDisabled: false,
    });
  }

  return allDays;
}

export interface ServiceDateRange {
  start: Dayjs | null;
  end: Dayjs | null;
}

/**
 * Displayed start/end date for a service, kept in sync with getServiceDays:
 * end is the last service day's date rather than the raw endTime, so an
 * overnight shift's date-range label (e.g. "Sep 02 - Sep 04") never shows a
 * trailing calendar day that has no shift starting on it.
 */
export function getServiceDateRange(
  startTime?: string,
  endTime?: string,
): ServiceDateRange {
  if (!startTime || !endTime) return { start: null, end: null };

  const days = getServiceDays(startTime, endTime);
  const end = days.length ? days[days.length - 1].dayjs : dayjs(endTime);

  return {
    start: dayjs(startTime),
    end,
  };
}

export const generateTimeStampOffset = (startDate: any, startTime: any) => {
  const newStartDate = `${new Date(startDate).getDate()}/${
    new Date(startDate).getMonth() + 1
  }/${new Date(startDate).getFullYear()}`;
  const newStartTime = `${new Date(startTime).getHours()}:${new Date(
    startTime,
  ).getMinutes()}`;

  const offset = moment(newStartDate + ' ' + newStartTime, 'DD/MM/YYYY H:mm')
    .toDate()
    .getTimezoneOffset();

  return offset;
};

const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Get time with timezone abbreviation
export const getTimeWithTimeZoneAbbreviationNew = (
  dateString: any,
  format = 'MMM D, YYYY hh:mm A (z)',
) => {
  return moment.utc(dateString).tz(deviceTimeZone).format(format);
};

export function getTimezoneAbbreviation() {
  const momentTz = require('moment-timezone');
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return momentTz().tz(timezone).format('z'); // Returns "EST", "PST", etc.
}

// Formats to "Aug 26, 2026 10:00 AM PST" for approval/rejection status copy.
export function formatApprovalActionDate(isoDate?: string): string {
  if (!isoDate) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(isoDate));

  const get = (type: string): string =>
    parts.find(part => part.type === type)?.value ?? '';

  return `${get('month')} ${get('day')}, ${get('year')} ${get('hour')}:${get(
    'minute',
  )} ${get('dayPeriod')} ${getTimeWithTimeZoneAbbreviationNew(isoDate, 'z')}`;
}

export function getStartDayTimestamp(date: any) {
  const day = dayjs(date).startOf('day').valueOf();
  return day;
}

export function getEndDayTimestamp(date: any) {
  const day = dayjs(date).endOf('day').valueOf();
  return day;
}

// Maps a dashboard graph filter ('today' | 'last7Days' | 'last30Days' | 'year'
// | 'allOrders') to a { fromDate, toDate } timestamp range for the spending
// graph APIs. 'today' is just the current day; 'last7Days'/'last30Days' are
// rolling windows ending today (inclusive, so 'last7Days' spans today + 6
// prior days); 'year' (Total Spend to Date) leaves fromDate unset so the API
// returns spend with no lower bound. 'allOrders' (All Orders) leaves toDate
// unset entirely and uses a fixed far-past fromDate (mirrors
// getAllOrdersFromDate in the Spendings module) so it isn't capped at today.
export function getGraphFilterDateRange(filterValue: string): {
  fromDate?: number;
  toDate?: number;
} {
  const today = dayjs();

  switch (filterValue) {
    case 'today':
      return {
        fromDate: today.startOf('day').valueOf(),
        toDate: today.endOf('day').valueOf(),
      };
    case 'last7Days':
      return {
        fromDate: today.subtract(6, 'day').startOf('day').valueOf(),
        toDate: today.endOf('day').valueOf(),
      };
    case 'last30Days':
      return {
        fromDate: today.subtract(29, 'day').startOf('day').valueOf(),
        toDate: today.endOf('day').valueOf(),
      };
    case 'allOrders':
      return {
        fromDate: dayjs('2000-01-01').startOf('day').valueOf(),
      };
    case 'year':
    default:
      return {
        toDate: today.endOf('day').valueOf(),
      };
  }
}

// `dateStr` is a UTC-anchored calendar-date string (e.g. "2024-01-01", see
// formatDateUTC) representing a specific day regardless of viewer timezone.
// Parsing it directly with dayjs would read it as UTC midnight and then
// derive "local" day boundaries around that instant, which shifts into the
// wrong local calendar day depending on the viewer's UTC offset. Read the
// Y/M/D directly and build local boundaries from those instead.
export function getLocalDayBoundsFromUTCDateString(dateStr: string): {
  start: number;
  end: number;
} {
  const [year, month, day] = dateStr.split('-').map(Number);
  return {
    start: new Date(year, month - 1, day, 0, 0, 0, 0).getTime(),
    end: new Date(year, month - 1, day, 23, 59, 59, 999).getTime(),
  };
}
export function getTimeDisplay(
  shifts: any[],
  isFromDefault?: boolean,
): { startVaries: boolean; endVaries: boolean } {
  if (!shifts || shifts.length === 0)
    return { startVaries: false, endVaries: false };

  // Helper to extract and compare times for a given type
  function checkVariation(type: 'start' | 'end'): boolean {
    let times: any[];
    if (isFromDefault) {
      times = shifts
        .map(shift =>
          type === 'start' ? shift?.actualStartTime : shift?.actualEndTime,
        )
        .filter(t => !!t);
    } else {
      times = shifts
        .map(shift =>
          type === 'start'
            ? shift?.original?.actualStartTime
            : shift?.original?.actualEndTime,
        )
        .filter(t => !!t);
    }

    if (times.length === 0) return false;

    const first = times[0];
    return !times.every(
      time =>
        new Date(time).getHours() === new Date(first).getHours() &&
        new Date(time).getMinutes() == new Date(first).getMinutes() &&
        new Date(time).getSeconds() == new Date(first).getSeconds(),
    );
  }

  const startVaries = checkVariation('start');
  const endVaries = checkVariation('end');
  console.log('startVaries', startVaries, 'endVaries', endVaries);
  return { startVaries, endVaries };
}
export const getCoverage = val => {
  const value = dayjs(val).format('HH:mm');
  const [hourStr, minuteStr] = value.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return undefined; // or handle invalid time
  }

  const minutesSinceMidnight = hour * 60 + minute;

  if (minutesSinceMidnight >= 180 && minutesSinceMidnight < 900) {
    return `Day`;
  }
  return `Night`;
};

export const formatPriceBreakupValues = (price: number) => {
  return Number(price).toLocaleString('en-US', {
    minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Determines whether a month-year header should be displayed
 * for the current item in a list, based on its `createdAt` date.
 *
 * @param currentItemDate - The current item's createdAt date
 * @param previousItemDate - The previous item's createdAt date (optional)
 * @returns An object containing:
 *   - showHeader: boolean — whether to render the month-year header
 *   - formattedDate: string — formatted month-year string (e.g., "October, 2025")
 */
export const getMonthYearHeader = (
  currentItemDate: string | Date,
  previousItemDate?: string | Date,
) => {
  const currentFormatted = dayjs(currentItemDate).format('MMMM, YYYY');
  const prevFormatted = previousItemDate
    ? dayjs(previousItemDate).format('MMMM, YYYY')
    : null;

  return {
    showHeader: currentFormatted !== prevFormatted,
    formattedDate: currentFormatted,
  };
};

/**
 * Capitalizes the first letter of each word in a string.
 * Example: "CREDIT CARD" → "Credit Card"
 */
export const formatToTitleCase = (value?: string): string => {
  if (!value) return '';

  return (
    value
      // normalize separators to spaces
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

export const formatStringToTitleCase = (value?: string): string => {
  if (!value) return '';

  return value.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

export const formatDateUs = (isoString: string) => {
  const date = new Date(isoString);

  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: 'America/Los_Angeles', // PST/PDT auto-handled
  };

  const formatted = new Intl.DateTimeFormat('en-US', options).format(date);

  return `${formatted} (PST)`;
};

export const formatDateUsWithTimeZone = (
  timestamp: number | string,
  timeZone = 'America/Los_Angeles', // default timezone
) => {
  const date =
    typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);

  if (isNaN(date.getTime())) return ''; // invalid date

  timestamp = `${timestamp}Z`;

  // Format date part (month/day/year)
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  };
  const formattedDate = new Intl.DateTimeFormat(undefined, dateOptions).format(
    date,
  );

  // Format time part (hour:minute AM/PM)
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  };
  const formattedTime = date.toLocaleTimeString(undefined, timeOptions);

  return `${formattedDate} ${formattedTime}`;
};

// export const formattedDateUs = (
//   isoString?: string,
//   timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
// ) => {
//   if (!isoString) return '--';

//   const date = new Date(isoString);
//   if (isNaN(date.getTime())) return '--'; // invalid date fallback

//   let options: Intl.DateTimeFormatOptions = {
//     month: 'short',
//     day: 'numeric',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: 'numeric',
//     hour12: true,
//     timeZone,
//     timeZoneName: 'short',
//   };

//   try {
//     return new Intl.DateTimeFormat('en-US', options).format(date);
//   } catch (err) {
//     // If an invalid timezone is passed
//     console.warn(`Invalid timezone: ${timeZone}, using system timezone.`);
//     delete options.timeZone;
//     return new Intl.DateTimeFormat('en-US', options).format(date);
//   }
// };

export const formattedDateUs = (
  isoString?: string,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => {
  if (!isoString) return '--';

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '--';

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);

    // Extract the abbreviation only (IST, PST, EST, GMT+5:30)
    const tz = parts.find(p => p.type === 'timeZoneName')?.value || '';

    // Format without the timezone name
    const formattedWithoutTZ = parts
      .filter(p => p.type !== 'timeZoneName')
      .map(p => p.value)
      .join('')
      .trim();

    return `${formattedWithoutTZ} (${tz})`;
  } catch {
    console.warn('Invalid timezone:', timeZone);
    return '--';
  }
};

export const formatDateWithWeekdayAndTimeZone = (
  timestamp?: string | number,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone, // viewer's local timezone
): string => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    });

    const parts = formatter.formatToParts(date);
    const get = (type: string) => parts.find(p => p.type === type)?.value || '';

    return `${get('weekday')}, ${get('month')} ${get('day')}, ${get(
      'year',
    )} – ${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
  } catch {
    console.warn('Invalid timezone:', timeZone);
    return '';
  }
};

export const formatSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

export const camelToTitle = (value = ''): string => {
  if (!value) return '';

  return value
    .replace(/([A-Z])/g, ' $1') // insert space before capital letters
    .replace(/_/g, ' ') // convert underscores to spaces (optional)
    .trim() // remove leading/trailing spaces
    .toLowerCase() // convert to lowercase
    .replace(/\b\w/g, char => char.toUpperCase()); // Title Case
};
export function timeAgo(timestamp: number | string, text?: string) {
  const now = Date.now();

  // Convert if string
  const time =
    typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;

  const diff = now - time;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  const label = text || 'Updated';

  if (minutes < 1) return `${label} just now`;
  if (minutes < 60) return `${label} ${minutes} min ago`;
  if (hours < 24) return `${label} ${hours} hr ago`;
  return `${label} ${days} days ago`;
}

export function timeAgoWithoutText(timestamp: number | string) {
  const now = Date.now();

  // Convert if string
  const time =
    typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;

  const diff = now - time;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return `just now`;
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hr ago`;
  return `${days} days ago`;
}

export const calculatedServicePrice = (data: any, shifts: any) => {
  if (!data?.serviceCancellationPolicies || !shifts?.length)
    return {
      totalCost: 0,
    };

  const now = moment();
  const nonCancelledShifts = [...shifts].filter(
    (shift: any) =>
      shift.status != 'CANCELLED' && shift.status != 'CANCEL_REQUESTED',
  );
  const sortedShifts = [...nonCancelledShifts].sort((a, b) => {
    const aStart = moment(a?.actualStartTime);
    const bStart = moment(b?.actualStartTime);
    return aStart.diff(bStart);
  });
  const chargedThresholds = new Set<number>();
  //   clone before sort
  const basePolicies = [...data.serviceCancellationPolicies].sort(
    (a, b) => a.serviceCancellationThreshold - b.serviceCancellationThreshold,
  );

  let totalCost = 0;

  // if (serviceCancellationChargePolicy === 'PER_UNIT') {

  sortedShifts.forEach(shift => {
    // Apply policy filtering per-shift (mixed selection: flexible + non-flexible)
    const policies = shift?.isFlexibleShiftHr
      ? basePolicies.filter((p: any) => p?.serviceCancellationType !== 'HOURLY')
      : basePolicies;
    const shiftDiff = moment(shift.actualStartTime).diff(now, 'hours', true);

    if (shiftDiff <= 0) return; // skip past shifts

    const policyIndex = policies.findIndex(
      p => shiftDiff <= p.serviceCancellationThreshold,
    );

    if (policyIndex === -1) return;

    const policy = policies[policyIndex];

    const threshold = policy.serviceCancellationThreshold;
    if (policy.serviceCancellationChargePolicy === 'PER_UNIT') {
      // PER_UNIT → always add cost for each shift
      if (policy.serviceCancellationType === 'FLAT') {
        totalCost += Number(policy.serviceCancellationFee);
      } else {
        totalCost +=
          data?.rateModel?.rentPrice * Number(policy.serviceCancellationFee);
      }
    } else {
      if (policy.serviceCancellationType === 'PERCENTAGE') {
        if (!chargedThresholds.has(threshold)) {
          totalCost +=
            (shift?.cost * Number(policy.serviceCancellationFee)) / 100;
          chargedThresholds.add(threshold);
        }
      } else if (policy.serviceCancellationType === 'FLAT') {
        if (!chargedThresholds.has(threshold)) {
          totalCost += Number(policy.serviceCancellationFee);
          chargedThresholds.add(threshold);
        }
      } else {
        if (!chargedThresholds.has(threshold)) {
          totalCost +=
            data?.rateModel?.rentPrice * Number(policy.serviceCancellationFee);
          chargedThresholds.add(threshold);
        }
      }
    }
  });

  return {
    totalCost,
    upcomingNumberOfShifts: shifts.filter((shift: any) =>
      moment(shift?.actualStartTime).isAfter(now),
    ).length,
  };
};

export const calculatedPrice = (data: any) => {
  if (!data) return 0;

  if (data?.shoppingMode === 'RENT') {
    // if (data?.status == 'PLACED') return 0;
    const daysUntilStart = moment(data?.startDate).diff(moment(), 'days'); //  use days
    const threshold = Number(data?.rentalCancellationThreshold) || 0;
    const cancellationFee = Number(data?.rentalCancellationFee) || 0;
    if (daysUntilStart <= threshold) {
      if (data?.rentalCancellationType === 'FLAT') {
        return cancellationFee;
      } else {
        const itemTotal = Number(data?.itemTotal) || 0;
        return itemTotal * (cancellationFee / 100);
      }
    }
    return 0;
  } else if (data?.shoppingMode === 'BUY') {
    let cancellationFee = 0;
    let feeType = 'FLAT';
    if (data?.status === ORDER_PLACED_STATUS.SHIPPED) {
      cancellationFee = Number(data?.shippedProductCancellationFee) || 0;
      feeType = data?.shippedProductCancellationType || 'FLAT';
    } else if (data?.status === ORDER_PLACED_STATUS.INTRANSIT) {
      cancellationFee = Number(data?.inTransitProductCancellationFee) || 0;
      feeType = data?.inTransitProductCancellationType || 'FLAT';
    }
    if (feeType === 'FLAT') {
      return cancellationFee;
    } else {
      const itemTotal = Number(data?.itemTotal) || 0;
      return itemTotal * (cancellationFee / 100);
    }
  }
};

export const getThreatColor = (threats: any) => {
  if (!threats) return '';

  if (threats.CRITICAL > 0) return 'red';
  if (threats.HIGH > 0) return 'orange';
  if (threats.MEDIUM > 0) return 'orange';
  if (threats.LOW > 0) return 'green';

  return 'green'; // fallback if no threats
};

export const formateAddress = (item: any) => {
  const rawAddress = `${item?.address?.addressLine1 ?? ''}
    ${item?.address?.addressLine2 ?? ''}
    ${item?.address?.cityTown ?? ''}, ${item?.address?.stateProvince ?? ''} ${
    item?.address?.postalCode ?? ''
  }
    ${item?.address?.country ?? ''}`;

  return rawAddress
    .split('\n')
    .map(line => line.trim())
    .filter(line => line)
    .map((line, index, arr) => (index < arr.length - 1 ? `${line},` : line))
    .join('\n');
};

export const downloadPdf = async (url: string, fileName?: string) => {
  const response = await fetch(url);
  const blob = await response.blob();

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'test';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(blobUrl);
};

export const getDeliveryByDate = (eta?: number) => {
  if (!eta) return '--';

  return dayjs().add(eta, 'day').format('ddd MMM D, YYYY');
};

export const groupByMonth = (items: any[]) => {
  const groups: { [key: string]: any[] } = {};
  items?.forEach(item => {
    const month = dayjs(item.created).format('MMMM');
    if (!groups[month]) groups[month] = [];
    groups[month].push(item);
  });
  return Object.entries(groups)?.sort(
    ([a], [b]) => dayjs(b, 'MMMM').valueOf() - dayjs(a, 'MMMM').valueOf(),
  );
};

export const getAllCartProducts = (cart: any[] = []) => {
  return cart.flatMap(
    branch =>
      branch?.groupedProducts?.flatMap(
        (group: any[]) => group?.products ?? [],
      ) ?? [],
  );
};

export const segregateAndSummarizeCart = (
  products: CartProduct[] = [],
): SegregatedCart => {
  return products.reduce(
    (acc, product) => {
      const mode = product.shoppingMode;
      const price = product.finalPrice ?? product.priceBreakup?.itemTotal ?? 0;
      const qty = product.qty ?? 1;

      switch (mode) {
        case SHOPPING_MODE.BUY:
          acc.BUY.push(product);
          acc.counts.BUY += 1;
          acc.totals.BUY += price * qty;
          break;
        case SHOPPING_MODE.RENT:
          acc.RENT.push(product);
          acc.counts.RENT += 1;
          acc.totals.RENT += price * qty;
          break;
        case SHOPPING_MODE.SERVICE:
          acc.SERVICE.push(product);
          acc.counts.SERVICE += 1;
          acc.totals.SERVICE += price * qty;
          break;
      }

      return acc;
    },
    {
      BUY: [],
      RENT: [],
      SERVICE: [],
      counts: { BUY: 0, RENT: 0, SERVICE: 0 },
      totals: { BUY: 0, RENT: 0, SERVICE: 0 },
    } as SegregatedCart,
  );
};

export const differenceInDaysAndHours = (
  startDate: string,
  endDate: string,
) => {
  const difference =
    new Date(endDate).getTime() - new Date(startDate).getTime();
  let days = Math.round(difference / (1000 * 60 * 60 * 24));
  days += 1;

  if (days <= 1) {
    return '1 Day';
  }
  if (days > 1) {
    return days + ' Days';
  }
  return '';
};

export const convertFormateDate = (date: any, format: string) => {
  return moment(date).format(format);
};

export const pluralize = (word: string, count: number) => {
  if (count === 1) return word;

  if (word.endsWith('y')) return word.slice(0, -1) + 'ies';
  if (word.endsWith('s')) return word + 'es';

  return word + 's';
};

export const getLabel = (count: number, label: string) => {
  if (label.includes('/')) {
    const parts = label.split('/');
    const pluralParts = parts.map(part => pluralize(part, count));
    return `${count} ${pluralParts.join('/')}`;
  }

  return `${count} ${pluralize(label, count)}`;
};

export const formatCurrency = (value: number) => {
  const formatNumber = (num: number) =>
    Number.isInteger(num) ? num.toString() : num.toFixed(1);

  if (value >= 1_000_000_000) {
    const formatted = value / 1_000_000_000;
    return `$${formatNumber(formatted)}B`;
  }

  if (value >= 1_000_000) {
    const formatted = value / 1_000_000;
    return `$${formatNumber(formatted)}M`;
  }

  if (value >= 1_000) {
    const formatted = value / 1_000;
    return `$${formatNumber(formatted)}K`;
  }

  return `$${value.toFixed(0)}`;
};

/**
 * Returns a singular or plural label based on the count.
 *
 * Examples:
 * pluralize(1, "Hour") => "1 Hour"
 * pluralize(2, "Hour") => "2 Hours"
 * pluralize(0, "Hour") => "0 Hours"
 */
export const pluralizeIt = (
  count: number,
  singular: string,
  plural?: string,
): string => {
  return `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;
};

/**
 * Like pluralizeIt, but formats the count via formatPriceBreakupValues
 * (thousands separators, up to 2 decimal places) instead of printing it raw
 * — for counts that can be large or fractional (e.g. total hours, costs).
 *
 * Examples:
 * formatAndPluralize(1, "hr") => "1 hr"
 * formatAndPluralize(1234.5, "hr") => "1,234.5 hrs"
 */
export const formatAndPluralize = (
  count: number,
  singular: string,
  plural?: string,
): string => {
  return `${formatPriceBreakupValues(count)} ${
    count === 1 ? singular : plural ?? `${singular}s`
  }`;
};

export const formatNames = (arr: string[] | any) => {
  if (!arr || arr.length === 0) return { text: '', extraCount: 0 };
  if (arr.length <= 2) return { text: arr.join(', '), extraCount: 0 };

  return { text: `${arr[0]}, ${arr[1]}`, extraCount: arr.length - 2 };
};

export const formatVarianceAmount = (value: number, actualSpending: number) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  const hasActualSpending = Number(actualSpending) !== 0;
  const sign = hasActualSpending && safeValue > 0 ? '-' : '';
  const absValue = Math.abs(safeValue);
  const formatted = Number.isInteger(absValue)
    ? absValue.toLocaleString('en-US')
    : absValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return `${sign}$${formatted}`;
};

export const formatBranchAddress = (branchInfo?: BranchInfo): string => {
  const company = branchInfo?.company;

  return [
    company?.businessAddressLine1,
    company?.businessAddressLine2,
    company?.businessCity,
    company?.businessState,
    company?.businessZipCode,
    company?.businessLocation,
  ]
    .filter(Boolean)
    .join(', ');
};
